import { useEffect, useState } from "react";
import { Save, RotateCcw, ExternalLink, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { ADMIN } from "@/constants/testIds/landing";

function Field({ label, value, onChange, multiline, placeholder, testId }) {
  const Comp = multiline ? "textarea" : "input";
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#1A1A1A] mb-1.5">
        {label}
      </span>
      <Comp
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={multiline ? 3 : undefined}
        data-testid={testId}
        className="rounded-2xl border border-[#E5E5E5] bg-white px-4 py-2.5 w-full text-sm focus:ring-2 focus:ring-[#6B8E23] focus:border-transparent outline-none transition-all resize-none"
      />
    </label>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="bg-white rounded-3xl border border-[#EAEAEA] p-6 sm:p-8">
      <h3 className="font-[Outfit] text-lg font-semibold">{title}</h3>
      {subtitle ? (
        <p className="text-xs text-[#888] mt-0.5 mb-5">{subtitle}</p>
      ) : (
        <div className="mb-5" />
      )}
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export default function AdminContent() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const { data } = await adminApi.get("/admin/content");
      setContent(data);
      setDirty(false);
    } catch (e) {
      toast.error("Inhalte konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patch = (updater) => {
    setContent((c) =>
      typeof updater === "function" ? updater(c) : { ...c, ...updater }
    );
    setDirty(true);
  };

  const setHero = (key, val) =>
    patch((c) => ({ ...c, hero: { ...c.hero, [key]: val } }));

  const setImpressum = (key, val) =>
    patch((c) => ({ ...c, impressum: { ...(c.impressum || {}), [key]: val } }));

  const setSectionTitle = (si, val) =>
    patch((c) => ({
      ...c,
      sections: c.sections.map((s, i) =>
        i === si ? { ...s, title: val } : s
      ),
    }));

  const setSectionItem = (si, ii, key, val) =>
    patch((c) => ({
      ...c,
      sections: c.sections.map((s, i) =>
        i === si
          ? {
              ...s,
              items: s.items.map((it, j) =>
                j === ii ? { ...it, [key]: val } : it
              ),
            }
          : s
      ),
    }));

  const addSectionItem = (si) =>
    patch((c) => ({
      ...c,
      sections: c.sections.map((s, i) =>
        i === si
          ? { ...s, items: [...s.items, { lead: "Neuer Punkt", text: "Beschreibung..." }] }
          : s
      ),
    }));

  const removeSectionItem = (si, ii) =>
    patch((c) => ({
      ...c,
      sections: c.sections.map((s, i) =>
        i === si ? { ...s, items: s.items.filter((_, j) => j !== ii) } : s
      ),
    }));

  const setPerk = (pi, key, val) =>
    patch((c) => ({
      ...c,
      perks: c.perks.map((p, i) => (i === pi ? { ...p, [key]: val } : p)),
    }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/admin/content", content);
      toast.success("Inhalte gespeichert. Landing Page aktualisiert sich beim Neuladen.");
      setDirty(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Speichern fehlgeschlagen.";
      toast.error(typeof msg === "string" ? msg : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Wirklich alle Inhalte auf Standard zurücksetzen? (Impressum bleibt erhalten)")) return;
    try {
      const { data } = await adminApi.post("/admin/content/reset");
      setContent(data);
      setDirty(false);
      toast.success("Inhalte zurückgesetzt.");
    } catch (e) {
      toast.error("Zurücksetzen fehlgeschlagen.");
    }
  };

  if (loading || !content) {
    return (
      <div className="bg-white rounded-3xl border border-[#EAEAEA] p-8 text-center text-[#888]">
        Lädt Inhalte…
      </div>
    );
  }

  return (
    <div data-testid={ADMIN.contentEditor} className="space-y-6">
      {/* Sticky action bar */}
      <div className="sticky top-16 z-20 bg-[#FAFAFA]/95 backdrop-blur-xl -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 border-b border-[#EAEAEA] flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">
            {dirty ? (
              <span className="text-[#B45A00]">● Ungespeicherte Änderungen</span>
            ) : (
              <span className="text-[#3A7D1E]">✓ Alles gespeichert</span>
            )}
          </p>
          <p className="text-xs text-[#888]">
            Nach dem Speichern die Landing Page neu laden, um Änderungen zu sehen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border border-[#E5E5E5] hover:border-[#6B8E23] hover:text-[#6B8E23] transition-colors"
          >
            <ExternalLink size={14} /> Vorschau
          </a>
          <button
            onClick={reset}
            data-testid={ADMIN.contentReset}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border border-[#E5E5E5] hover:border-[#B42318] hover:text-[#B42318] transition-colors"
          >
            <RotateCcw size={14} /> Zurücksetzen
          </button>
          <button
            onClick={save}
            disabled={saving || !dirty}
            data-testid={ADMIN.contentSave}
            className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold bg-[#6B8E23] text-white hover:bg-[#55711C] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={14} /> {saving ? "Speichert…" : "Speichern"}
          </button>
        </div>
      </div>

      {/* HERO */}
      <Section title="Hero-Bereich" subtitle="Der erste Eindruck deiner Landing Page">
        <Field
          label="Badge (kleine Pille oben)"
          value={content.hero.badge}
          onChange={(v) => setHero("badge", v)}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Überschrift – Zeile 1"
            value={content.hero.heading_line1}
            onChange={(v) => setHero("heading_line1", v)}
          />
          <Field
            label="Überschrift – Zeile 2 (grün hervorgehoben)"
            value={content.hero.heading_line2}
            onChange={(v) => setHero("heading_line2", v)}
          />
        </div>
        <Field
          label="Untertitel"
          value={content.hero.subheading}
          onChange={(v) => setHero("subheading", v)}
          multiline
        />
        <Field
          label="Button-Text (Hero)"
          value={content.hero.cta_button}
          onChange={(v) => setHero("cta_button", v)}
        />
        <Field
          label="Download-Link (Google Play Store URL) – für alle Buttons"
          value={content.download_link}
          onChange={(v) => patch({ download_link: v })}
          placeholder="https://play.google.com/store/apps/details?id=..."
        />
        <Field
          label="Hero-Bild URL"
          value={content.hero.image_url}
          onChange={(v) => setHero("image_url", v)}
          testId={ADMIN.contentHeroImage}
        />
        {content.hero.image_url && (
          <div className="rounded-2xl overflow-hidden border border-[#E5E5E5] bg-white max-w-md">
            <img
              src={content.hero.image_url}
              alt="Vorschau"
              className="w-full h-44 object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        )}
        <Field
          label="Bild-Beschreibung (Alt-Text)"
          value={content.hero.image_alt}
          onChange={(v) => setHero("image_alt", v)}
        />
      </Section>

      {/* FUNKTIONSBEREICH */}
      <Section
        title="Funktionsbereich"
        subtitle="Überschrift + die großen Funktionsblöcke mit Aufzählungspunkten"
      >
        <Field
          label="Section-Überschrift"
          value={content.features_heading}
          onChange={(v) => patch({ features_heading: v })}
        />
        <Field
          label="Section-Untertitel"
          value={content.features_subheading}
          onChange={(v) => patch({ features_subheading: v })}
          multiline
        />

        <div className="space-y-6 pt-2">
          {(content.sections || []).map((section, si) => (
            <div
              key={si}
              className="rounded-2xl border border-[#EAEAEA] bg-[#FAFAFA] p-4 sm:p-5 space-y-4"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-[#6B8E23]">
                Block {si + 1}
              </p>
              <Field
                label="Block-Titel"
                value={section.title}
                onChange={(v) => setSectionTitle(si, v)}
              />
              <div className="space-y-3">
                {section.items.map((item, ii) => (
                  <div
                    key={ii}
                    className="rounded-xl border border-[#E5E5E5] bg-white p-3 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#888]">Punkt {ii + 1}</span>
                      <button
                        onClick={() => removeSectionItem(si, ii)}
                        className="inline-flex items-center gap-1 text-xs text-[#888] hover:text-[#B42318] transition-colors"
                      >
                        <Trash2 size={13} /> Entfernen
                      </button>
                    </div>
                    <Field
                      label="Fettgedruckter Anfang (Lead)"
                      value={item.lead}
                      onChange={(v) => setSectionItem(si, ii, "lead", v)}
                    />
                    <Field
                      label="Beschreibung"
                      value={item.text}
                      onChange={(v) => setSectionItem(si, ii, "text", v)}
                      multiline
                    />
                  </div>
                ))}
                <button
                  onClick={() => addSectionItem(si)}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border border-dashed border-[#C9CFC1] text-[#6B8E23] hover:bg-[#E8EFE1] transition-all"
                >
                  <Plus size={14} /> Punkt hinzufügen
                </button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* VORTEILE */}
      <Section title="Weitere Vorteile" subtitle="Die kleinen Highlight-Karten">
        <Field
          label="Section-Überschrift"
          value={content.perks_heading}
          onChange={(v) => patch({ perks_heading: v })}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          {(content.perks || []).map((perk, pi) => (
            <div
              key={pi}
              className="rounded-2xl border border-[#EAEAEA] bg-[#FAFAFA] p-4 space-y-3"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-[#6B8E23]">
                Vorteil {pi + 1}
              </p>
              <Field
                label="Titel"
                value={perk.title}
                onChange={(v) => setPerk(pi, "title", v)}
              />
              <Field
                label="Beschreibung"
                value={perk.text}
                onChange={(v) => setPerk(pi, "text", v)}
                multiline
              />
            </div>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section title="Finaler Call-to-Action" subtitle="Der dunkle Block ganz unten">
        <Field
          label="Überschrift"
          value={content.final_cta_heading}
          onChange={(v) => patch({ final_cta_heading: v })}
        />
        <Field
          label="Untertitel"
          value={content.final_cta_subheading}
          onChange={(v) => patch({ final_cta_subheading: v })}
          multiline
        />
        <Field
          label="Button-Text"
          value={content.final_cta_button}
          onChange={(v) => patch({ final_cta_button: v })}
        />
      </Section>

      {/* FOOTER */}
      <Section title="Footer" subtitle="Tagline + Kontakt-E-Mail">
        <Field
          label="Tagline / Kurzbeschreibung"
          value={content.footer_tagline}
          onChange={(v) => patch({ footer_tagline: v })}
          multiline
        />
        <Field
          label="Kontakt-E-Mail"
          value={content.contact_email}
          onChange={(v) => patch({ contact_email: v })}
        />
      </Section>

      {/* IMPRESSUM */}
      <Section
        title="Impressum"
        subtitle="Pflichtangaben gemäß § 5 DDG (TMG) – erscheinen auf /impressum"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Name / Firmenname *"
            value={content.impressum?.name}
            onChange={(v) => setImpressum("name", v)}
            placeholder="z.B. Max Mustermann"
          />
          <Field
            label="Land"
            value={content.impressum?.country}
            onChange={(v) => setImpressum("country", v)}
            placeholder="Deutschland"
          />
        </div>
        <Field
          label="Straße und Hausnummer *"
          value={content.impressum?.street}
          onChange={(v) => setImpressum("street", v)}
          placeholder="z.B. Musterstraße 12"
        />
        <Field
          label="PLZ und Ort *"
          value={content.impressum?.postal_city}
          onChange={(v) => setImpressum("postal_city", v)}
          placeholder="z.B. 10115 Berlin"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Telefon"
            value={content.impressum?.phone}
            onChange={(v) => setImpressum("phone", v)}
            placeholder="z.B. +49 30 1234567"
          />
          <Field
            label="E-Mail (Impressum)"
            value={content.impressum?.email}
            onChange={(v) => setImpressum("email", v)}
            placeholder="Lass leer, um Kontakt-E-Mail zu verwenden"
          />
        </div>
        <Field
          label="USt-IdNr. (optional)"
          value={content.impressum?.ust_id}
          onChange={(v) => setImpressum("ust_id", v)}
          placeholder="z.B. DE123456789 – leer lassen, wenn nicht vorhanden"
        />
        <div className="pt-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#888] mb-2">
            Verantwortlich nach § 18 MStV (optional – sonst Daten von oben)
          </p>
        </div>
        <Field
          label="Name (verantwortlich)"
          value={content.impressum?.responsible_name}
          onChange={(v) => setImpressum("responsible_name", v)}
          placeholder="Leer = identisch mit Name oben"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Straße + Hausnr. (verantwortlich)"
            value={content.impressum?.responsible_street}
            onChange={(v) => setImpressum("responsible_street", v)}
            placeholder="Leer = identisch mit oben"
          />
          <Field
            label="PLZ + Ort (verantwortlich)"
            value={content.impressum?.responsible_postal_city}
            onChange={(v) => setImpressum("responsible_postal_city", v)}
            placeholder="Leer = identisch mit oben"
          />
        </div>
        <p className="text-xs text-[#888]">
          * Pflichtfelder gemäß § 5 DDG. Solange Name, Straße und PLZ/Ort leer
          sind, zeigt /impressum einen Hinweis-Banner statt der Daten.
        </p>
      </Section>
    </div>
  );
}
